import React from "react";
import styled from "styled-components";
import IconButton from "@material-ui/core/IconButton";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { CountryFlag } from "../Workspace/CountryFlag";
import SiteFavicon from "./SiteFavicon";
import LastCrawlBadge from "./LastCrawlBadge";

const TitlePaper = styled.div`
    position: relative;
    display: flex;
    align-items: stretch;
    justify-content: space-between;
    gap: 20px;
    background:
        radial-gradient(ellipse 600px 200px at 0% 0%, rgba(99, 102, 241, 0.05), transparent 60%),
        linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    padding: 18px 20px 18px 28px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 0 0 1px rgba(15, 23, 42, 0.02);
    overflow: hidden;
    margin-bottom: 24px;
    transition: box-shadow 200ms ease;

    &:hover {
        box-shadow: 0 6px 20px rgba(15, 23, 42, 0.08);
    }

    &::before {
        content: "";
        position: absolute;
        left: 0; top: 0; bottom: 0;
        width: 4px;
        background: linear-gradient(180deg, #6366f1 0%, #818cf8 100%);
    }

    @media (max-width: 600px) {
        flex-direction: column;
        align-items: stretch;
        gap: 14px;
        padding: 16px 14px 16px 20px;
        border-radius: 0;
        border-left: none;
        border-right: none;
    }
`;

const LeftCol = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 16px;
    @media (max-width: 600px) { gap: 12px; }
`;

const FaviconRing = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 6px;
    border-radius: 14px;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08), 0 0 0 1px rgba(15, 23, 42, 0.04);
    flex-shrink: 0;
`;

const TextStack = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    gap: 4px;
`;

const NameRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
`;

const Name = styled.h2`
    margin: 0;
    font-size: 1.625rem;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.2;
    letter-spacing: -0.5px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
    @media (max-width: 600px) { font-size: 1.35rem; }
`;

const UrlChip = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px 4px 12px;
    border-radius: 99px;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    color: #475569;
    font-size: 0.78rem;
    font-weight: 500;
    text-decoration: none;
    max-width: fit-content;
    transition: background 120ms, color 120ms, border-color 120ms;

    & svg { font-size: 14px; opacity: 0.6; transition: opacity 120ms; }

    &:hover {
        background: #eef2ff;
        border-color: #c7d2fe;
        color: #4338ca;
    }
    &:hover svg { opacity: 1; }
`;

const RightCol = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    flex-shrink: 0;
    @media (max-width: 600px) {
        align-items: flex-start;
    }
`;

const FlagWrap = styled.div`
    display: inline-flex;
    padding: 2px;
    border-radius: 4px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
    overflow: hidden;
    flex-shrink: 0;
`;

function normaliseUrl(raw = "") {
    if (!raw) return raw;
    return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
}

function UrlLink({ url }) {
    if (!url) return null;
    const href = normaliseUrl(url);
    const display = url.replace(/^https?:\/\//, "").replace(/\/$/, "");
    return (
        <UrlChip href={href} target="_blank" rel="noopener noreferrer" title={href}>
            {display}
            <OpenInNewIcon />
        </UrlChip>
    );
}

export default function WorkspaceHeader({ name, url, countryCode }) {
    return (
        <TitlePaper>
            <LeftCol>
                <FaviconRing>
                    <SiteFavicon url={url} name={name} size={48} />
                </FaviconRing>
                <TextStack>
                    <NameRow>
                        <Name>{name}</Name>
                        {countryCode && (
                            <FlagWrap><CountryFlag countryCode={countryCode} width={36} /></FlagWrap>
                        )}
                    </NameRow>
                    <UrlLink url={url} />
                </TextStack>
            </LeftCol>
            <RightCol>
                <LastCrawlBadge />
            </RightCol>
        </TitlePaper>
    );
}
